import React from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

import Dialog from 'src/components/dialog/Dialog';

export type SendStatus = 'pending' | 'sending' | 'success' | 'error';

export type SendProgressItem = {
    leadUuid: string;
    leadName: string;
    status: SendStatus;
    error?: string;
};

type Props = {
    open: boolean;
    onClose: () => void;
    items: SendProgressItem[];
    skippedItems?: SendProgressItem[];
    isDone: boolean;
};

export default function BulkSendProgressModal({ open, onClose, items, skippedItems = [], isDone }: Props) {
    return (
        <Dialog open={open}>
            <DialogTitle>
                {isDone ? 'Messages sent' : 'Sending messages…'}
            </DialogTitle>
            <DialogContent sx={{ overflowY: 'auto', maxHeight: 400 }}>
                <List dense disablePadding>
                    {items.map((item) => (
                        <ListItem
                            key={item.leadUuid}
                            disableGutters
                        >
                            <ListItemIcon sx={{ minWidth: 36 }}>
                                {item.status === 'pending' && <RadioButtonUncheckedIcon fontSize="small" color="disabled" />}
                                {item.status === 'sending' && <CircularProgress size={18} />}
                                {item.status === 'success' && <CheckCircleOutlineIcon fontSize="small" color="success" />}
                                {item.status === 'error' && (
                                    <Tooltip title={item.error ?? 'Failed to send'}>
                                        <ErrorOutlineIcon fontSize="small" color="error" />
                                    </Tooltip>
                                )}
                            </ListItemIcon>
                            <ListItemText primary={item.leadName || item.leadUuid} />
                        </ListItem>
                    ))}
                </List>
                {skippedItems.length > 0 && (
                    <>
                        <List dense disablePadding>
                            {skippedItems.map((item) => (
                                <ListItem key={item.leadUuid} disableGutters>
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                        <Tooltip title={item.error ?? 'Skipped'}>
                                            <ErrorOutlineIcon fontSize="small" color="disabled" />
                                        </Tooltip>
                                    </ListItemIcon>
                                    <ListItemText primary={item.leadName || item.leadUuid} />
                                </ListItem>
                            ))}
                        </List>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button variant="contained" onClick={onClose} disabled={!isDone}>
                    OK
                </Button>
            </DialogActions>
        </Dialog>
    );
}
